import * as React from 'react';
import { CssVarsProvider, useColorScheme } from '@mui/joy/styles';
import GlobalStyles from '@mui/joy/GlobalStyles';
import CssBaseline from '@mui/joy/CssBaseline';
import Box from '@mui/joy/Box';
import Button from '@mui/joy/Button';
import Checkbox from '@mui/joy/Checkbox';
import Divider from '@mui/joy/Divider';
import FormControl from '@mui/joy/FormControl';
import FormLabel, { formLabelClasses } from '@mui/joy/FormLabel';
import IconButton, { IconButtonProps } from '@mui/joy/IconButton';
import Input from '@mui/joy/Input';
import Typography from '@mui/joy/Typography';
import Stack from '@mui/joy/Stack';
import Alert from '@mui/joy/Alert';
import DarkModeRoundedIcon from '@mui/icons-material/DarkModeRounded';
import LightModeRoundedIcon from '@mui/icons-material/LightModeRounded';
import BadgeRoundedIcon from '@mui/icons-material/BadgeRounded';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@providers/AuthProvider';
// import GoogleIcon from './GoogleIcon';

interface FormElements extends HTMLFormControlsCollection {
    email: HTMLInputElement;
    password: HTMLInputElement;
    persistent: HTMLInputElement;
}
interface SignInFormElement extends HTMLFormElement {
    readonly elements: FormElements;
}

export default function Login() {
    const [isLoading, setIsLoading] = React.useState(false);
    const [formError, setFormError] = React.useState('');

    const [email, setEmail] = React.useState('');
    const [password, setPassword] = React.useState('');

    let navigate = useNavigate();
    let location = useLocation();
    let {signIn, user} = useAuth();

    let from = location.state?.from?.pathname || "/";

    // navigate users away from login screen if they end up here while logged in
    React.useEffect(() => {
      if(user) {
        navigate(from, { replace: true });
      }
    }, [user]);

    const handleSubmit = (event: React.FormEvent<SignInFormElement>) => {
        event.preventDefault();

        setFormError('');
        setIsLoading(true);

        signIn(email, password).then(() => {
            navigate(from, { replace: true });
            setIsLoading(false);
        }).catch((e) => {
            setIsLoading(false);
            setFormError(e.response.data.message ?? 'An unknown error occurred while logging in.');
        });

        const formElements = event.currentTarget.elements;
        const data = {
            email: formElements.email.value,
            password: formElements.password.value,
            persistent: formElements.persistent.checked,
        };
        // alert(JSON.stringify(data, null, 2));

        setTimeout(() => {

            setIsLoading(false);
        }, 1000);
    }

return (<>
        <Stack gap={2}>
            <Typography level="h3">Sign in</Typography>
            {formError && (<Alert variant={'soft'} color="danger">{formError}</Alert>)}
            <form
                onSubmit={handleSubmit}
            >
            <FormControl required>
                <FormLabel>Email</FormLabel>
                <Input
                    type="email"
                    name="email"
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                />
            </FormControl>
            <FormControl required sx={{mt: 2}}>
                <FormLabel>Password</FormLabel>
                <Input
                    type="password"
                    name="password"
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                />
            </FormControl>
            <Stack gap={2} sx={{ mt: 2 }}>
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        fontSize: 'sm',
                    }}
                >
                    <Checkbox size="sm" label="Remember me" name="persistent" />
                    {/* <Link to="/forgot-password"> */}
                    <Link to="https://conductor.maestrocm.com/#/auth/reset-password" target='_blank'>
                        Forgot your password?
                    </Link>
                </Box>
                <Button
                    type="submit"
                    loading={isLoading}
                    disabled={!email || !password}
                    fullWidth
                >
                    Sign in
                </Button>
            </Stack>
            </form>
        </Stack>
    </>);
}
